'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Footer } from './Footer';

export interface CommercialLandingContent {
  slug: string;
  eyebrow: string;
  title: string;
  description: string;
  formatLabel: string;
  highlights: Array<{ title: string; description: string }>;
  sourceExample: string;
  cardExample: string;
  relatedLinks: Array<{ href: string; label: string }>;
}

export function CommercialLanding({ content }: { content: CommercialLandingContent }) {
  const posthog = usePostHog();
  const viewed = useRef(false);
  const uploadHref = `/upload?utm_source=organic&utm_medium=landing_page&utm_campaign=${encodeURIComponent(content.slug)}`;

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    posthog?.capture('commercial_landing_viewed', {
      landing_page: content.slug,
      audience: 'medical_students',
      source_format: content.formatLabel,
    });
  }, [content.formatLabel, content.slug, posthog]);

  const captureCta = (placement: 'hero' | 'bottom') => {
    posthog?.capture('commercial_landing_cta_clicked', {
      landing_page: content.slug,
      placement,
      destination: '/upload',
      source_format: content.formatLabel,
    });
  };

  return (
    <main className="min-h-screen bg-background">
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{content.eyebrow}</p>
          <h1 className="mx-auto mt-4 max-w-3xl font-serif text-4xl font-bold leading-tight text-foreground sm:text-6xl">
            {content.title}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-muted sm:text-lg">{content.description}</p>
          <Link
            href={uploadHref}
            onClick={() => captureCta('hero')}
            className="ui-button ui-button--primary mt-8"
          >
            Try one lecture free
          </Link>
          <p className="mt-3 text-xs text-muted">No card is exported until you keep or edit it.</p>
        </div>
      </section>

      <section className="border-y border-border bg-surface px-5 py-16 sm:px-8 sm:py-20">
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
          {content.highlights.map((highlight, index) => (
            <article key={highlight.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              <span className="text-xs font-bold text-primary">0{index + 1}</span>
              <h2 className="mt-3 font-serif text-xl font-semibold text-foreground">{highlight.title}</h2>
              <p className="mt-2 text-sm leading-6 text-muted">{highlight.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Review before export</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">You decide what belongs in your deck</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-muted">
              SlideToAnki drafts cards from the lecture, then presents them one at a time. Keep, edit, reject, or flag an incorrect card before it reaches Anki.
            </p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-surface p-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Lecture source</p>
              <p className="mt-4 text-sm leading-7 text-foreground">{content.sourceExample}</p>
            </div>
            <div className="rounded-2xl border border-primary/25 bg-primary/5 p-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Draft study card</p>
              <p className="mt-4 text-sm leading-7 text-foreground">{content.cardExample}</p>
              <div className="mt-5 flex gap-2 text-xs font-semibold">
                <span className="rounded-lg border border-border bg-card px-3 py-2">Reject</span>
                <span className="rounded-lg border border-border bg-card px-3 py-2">Edit</span>
                <span className="rounded-lg bg-primary px-3 py-2 text-white">Keep</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card px-5 py-16 text-center sm:px-8 sm:py-20">
        <h2 className="font-serif text-3xl font-semibold text-foreground">Make the deck while the lecture is still fresh</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-muted">Upload {content.formatLabel.toLowerCase()}, review the drafts, and export the cards you want to study.</p>
        <Link
          href={uploadHref}
          onClick={() => captureCta('bottom')}
          className="ui-button ui-button--primary mt-7"
        >
          Upload a lecture
        </Link>
        <nav className="mx-auto mt-10 flex max-w-2xl flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-primary" aria-label="Related guides">
          {content.relatedLinks.map(link => <Link key={link.href} href={link.href} className="underline-offset-4 hover:underline">{link.label}</Link>)}
        </nav>
      </section>
      <Footer />
    </main>
  );
}
